import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ControlCandidate, RcmMapping, RcmParsed } from "../lib/rcmParser";
import {
  buildCandidates,
  parseRcmFile,
  suggestRcmMapping,
} from "../lib/rcmParser";

type RcmImportContextValue = {
  parsed: RcmParsed | null;
  // Name of the file `parsed` came from. Lives here (not in component
  // state) so a remounted upload card can never show an empty dropzone
  // while `parsed` still holds a previous file's data.
  fileName: string;
  mapping: RcmMapping;
  setMapping: (m: Partial<RcmMapping>) => void;
  setFileAndParse: (file: File) => Promise<void>;
  candidates: ControlCandidate[];
  clear: () => void;
  loading: boolean;
};

const defaultValue: RcmImportContextValue = {
  parsed: null,
  fileName: "",
  mapping: {},
  setMapping: () => {},
  setFileAndParse: async () => {},
  candidates: [],
  clear: () => {},
  loading: false,
};

const SESSION_KEY = "rcmImportState:v1";

const RcmImportContext = createContext<RcmImportContextValue>(defaultValue);

type Persisted = {
  parsed: RcmParsed | null;
  fileName?: string;
  mapping: RcmMapping;
};

function loadPersisted(): Persisted | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Persisted;
  } catch {
    return null;
  }
}

function persist(state: Persisted) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors (quota, etc.)
  }
}

export const RcmImportProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [parsed, setParsed] = useState<RcmParsed | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [mapping, setMappingState] = useState<RcmMapping>({});
  const [loading, setLoading] = useState(false);

  // Load from session on mount
  useEffect(() => {
    const persisted = loadPersisted();
    if (persisted) {
      setParsed(persisted.parsed);
      setFileName(persisted.fileName || "");
      setMappingState(persisted.mapping || {});
    }
  }, []);

  // Persist whenever state changes
  useEffect(() => {
    persist({ parsed, fileName, mapping });
  }, [parsed, fileName, mapping]);

  const setFileAndParse = useCallback(async (file: File) => {
    setLoading(true);
    try {
      const nextParsed = await parseRcmFile(file);
      setParsed(nextParsed);
      setFileName(file.name);
      const suggested = suggestRcmMapping(nextParsed.headers || []);
      // Use fresh suggestions for new file, don't merge with old cached mapping
      setMappingState(suggested);
    } finally {
      setLoading(false);
    }
  }, []);

  const setMapping = useCallback((m: Partial<RcmMapping>) => {
    setMappingState((prev) => ({ ...prev, ...m }));
  }, []);

  const candidates = useMemo(() => {
    if (!parsed) return [];
    return buildCandidates(parsed, mapping);
  }, [parsed, mapping]);

  const clear = useCallback(() => {
    setParsed(null);
    setFileName("");
    setMappingState({});
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      console.error("Error clearing RCM import context");
    }
  }, []);

  const value: RcmImportContextValue = {
    parsed,
    fileName,
    mapping,
    setMapping,
    setFileAndParse,
    candidates,
    clear,
    loading,
  };

  return (
    <RcmImportContext.Provider value={value}>
      {children}
    </RcmImportContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRcmImport = () => useContext(RcmImportContext);
